import * as Sentry from "@sentry/react";
import axios from "axios";
import type { AddPageLayoutData, CouponType } from "../../../src/types";
import { decrypt, encrypt } from "../services/encryptionService";
import { mockDataService } from "../services/mockDataService";
import { STORAGE_KEY } from "./constants";
import type { Entity, OrderFilters, ProviderType } from "./types";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const handleApiError = (error: any, context: string) => {
	if (axios.isAxiosError(error)) {
		const errorMessage = error.response?.data?.error || error.message;
		const statusCode = error.response?.status;
		const requestUrl = error.config?.url;

		Sentry.captureException(new Error(`API Error: ${context}`), {
			extra: {
				errorMessage,
				statusCode,
				requestUrl,
				context,
				response: error.response?.data,
			},
			tags: {
				type: "api_error",
				status: statusCode,
				endpoint: requestUrl,
			},
		});
	} else {
		Sentry.captureException(error, {
			extra: {
				context,
			},
			tags: {
				type: "unknown_error",
			},
		});
	}
	throw error;
};

export const api = {
	loginWithEmail: async (email: string, password: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/login`, { email, password });
			return response.data;
		} catch (error: any) {
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			handleApiError(error, "loginWithEmail");
		}
	},
	getOrders: async (filters: OrderFilters) => {
		try {
			const encryptedFilters = await encrypt(filters);
			const response = await axios.get(`${API_BASE_URL}/get_orders`, {
				params: { filters: encryptedFilters },
			});

			const decryptedResponse = await decrypt(response.data);
			return decryptedResponse;
		} catch (error: any) {
			handleApiError(error, "getOrders");
		}
	},
	getUserEntities: async (user_id: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/users/${user_id}/entities`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUserEntities");
		}
	},
	assignEntitiesToUser: async (user_id: string, entity_ids: number[]) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/users/${user_id}/entities`,
				{ entity_ids },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "assignEntitiesToUser");
		}
	},
	removeEntityFromUser: async (user_id: string, entity_id: number) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/users/${user_id}/entities/${entity_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "removeEntityFromUser");
		}
	},
	getRejectedOrders: async (params: {
		page?: number;
		limit?: number;
		keyword?: string;
		start_date?: string;
		end_date?: string;
	}) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_rejected_orders`, {
				params,
			});

			return response.data;
		} catch (error: any) {
			handleApiError(error, "getRejectedOrders");
		}
	},
	getAuditLogs: async (params: {
		entity_id?: number;
		table_name?: string;
		action?: string;
		start_date?: string;
		end_date?: string;
		limit?: number;
		offset?: number;
	}) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/audit_logs`, {
				params,
			});

			return response.data;
		} catch (error: any) {
			handleApiError(error, "getAuditLogs");
		}
	},
	getProviders: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_providers`);
			return response.data.results as ProviderType[];
		} catch (error: any) {
			handleApiError(error, "getProviders");
		}
	},
	createProvider: async (provider: any) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/providers/create`, provider);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createProvider");
		}
	},
	getProviderMargins: async (providerId: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/provider_margins/${providerId}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getProviderMargins");
		}
	},
	createProviderMargin: async (data: any) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/provider_margins`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createProviderMargin");
		}
	},
	updateProviderMargin: async (id: string, data: any) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/provider_margins/${id}`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateProviderMargin");
		}
	},
	deleteProviderMargin: async (id: string) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/provider_margins/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteProviderMargin");
		}
	},
	getGroupedOrders: async (filters: any) => {
		try {
			const encryptedFilters = await encrypt(filters);
			const response = await axios.get(`${API_BASE_URL}/get_grouped_orders`, {
				params: { filters: encryptedFilters },
			});

			const decryptedResponse = await decrypt(response.data);
			return decryptedResponse;
		} catch (error: any) {
			handleApiError(error, "getGroupedOrders");
		}
	},
	getOrder: async (invoice_number: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_order_detail/${invoice_number}`,
			);
			const decryptedResponse = await decrypt(response.data);
			return decryptedResponse;
		} catch (error: any) {
			handleApiError(error, "getOrder");
		}
	},
	updateOrderStatus: async (
		order_id: number,
		status: string,
		updated_by: string,
	) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_order_status_by_id`,
				{
					order_id,
					status,
					updated_by,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateOrderStatus");
		}
	},
	getEntities: async (order_id: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_entities/${order_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getEntities");
		}
	},
	transferOrder: async (
		invoice_number: string,
		entity_id: number,
		user_id: string,
		transfer_reason?: string,
	) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/transfer_order`, {
				invoice_number,
				transfer_to: entity_id,
				user_id,
				...(transfer_reason && { transfer_reason }),
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "transferOrder");
		}
	},
	subscribeToKnock: async (email: string, userId: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/knock/subscribe`, {
				email,
				userId,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "subscribeToKnock");
		}
	},
	getPharmacyStats: async (filters: any) => {
		try {
			const encryptedFilters = await encrypt(filters);
			const response = await axios.get(
				`${API_BASE_URL}/get_pharmacy_wise_stats`,
				{
					params: { filters: encryptedFilters },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getPharmacyStats");
		}
	},
	getStatusStats: (filters: any) => {
		return mockDataService.getStatusStats(filters);
	},
	searchPincodes: async (search_query: string, access_level: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/search_pincodes/${search_query}`,
				{ params: access_level ? { access_level } : undefined },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchPincodes");
		}
	},
	getAllEntities: async (entities_ids: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_all_entities`, {
				params: entities_ids !== "" ? { entities_ids } : undefined,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getAllEntities");
		}
	},
	getB2CEntities: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_b2c_entities`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getB2CEntities");
			return {
				success: false,
				results: [],
			};
		}
	},
	getEntityPincodes: async (entity_id: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/entities/pincodes/${entity_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getEntityPincodes");
		}
	},
	updateEntityPincodes: async (entity_id: number, pincode_ids: number[]) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.post(
				`${API_BASE_URL}/entities/update_pincode_list`,
				{ entity_id, pincode_ids },
				{
					headers: { 'x-user-email': userEmail },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateEntityPincodes");
		}
	},
	bulkUploadEntityPincodes: async (entity_id: number, pincode_list: string[]) => {
		try {
			const user = localStorage.getItem(STORAGE_KEY);

			let userEmail = "unknown";
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}

			const response = await axios.post(
				`${API_BASE_URL}/entities/bulk_upload_pincodes`,
				{ entity_id, pincode_list },
				{
					headers: {
						'x-user-email': userEmail,
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkUploadEntityPincodes");
		}
	},
	bulkDeleteEntityPincodes: async (entity_id: number, pincode_ids: number[]) => {
		try {
			const user = localStorage.getItem(STORAGE_KEY);

			let userEmail = "unknown";
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			} else {
				console.warn('[API] No user found in localStorage');
			}

			const response = await axios.post(
				`${API_BASE_URL}/entities/bulk_delete_pincodes`,
				{ entity_id, pincode_ids },
				{
					headers: {
						'x-user-email': userEmail,
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkDeleteEntityPincodes");
		}
	},
	createEntity: async (entity: Entity) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.post(
				`${API_BASE_URL}/entities/create`,
				entity,
				{
					headers: { 'x-user-email': userEmail },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createEntity");
		}
	},
	updateEntity: async (entity_id: number, entity: any) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.post(
				`${API_BASE_URL}/entities/update`,
				{ entity_id, ...entity },
				{
					headers: { 'x-user-email': userEmail },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateEntity");
		}
	},
	setDefaultPharmacy: async (entity_id: number) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.post(
				`${API_BASE_URL}/entities/set_default`,
				{ entity_id },
				{
					headers: { 'x-user-email': userEmail },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "setDefaultPharmacy");
		}
	},
	getAccessLevel: async (user_id: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_access_level/${user_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getAccessLevel");
		}
	},
	getUserList: async (user_id: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_user_list/${user_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUserList");
		}
	},
	getAccessLevels: async (user_id: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_access_levels/${user_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getAccessLevels");
		}
	},
	createAccess: async (data: any) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/create_access/`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createAccess");
		}
	},
	updateAccess: async (data: any) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/update_access`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateAccess");
		}
	},
	deleteAccess: async (id: number) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/delete_access/${id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteAccess");
		}
	},
	fetchPageLayouts: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_page_layouts`);
			// Validate response structure
			if (!Array.isArray(response.data)) {
				throw new Error("Invalid response format: expected array of layouts");
			}
			return response.data;
		} catch (error: any) {
			handleApiError(error, "fetchPageLayouts");
		}
	},
	createPageLayout: async (layout: AddPageLayoutData) => {
		try {
			// Validate layout data before sending
			if (!layout.page || !layout.metadata) {
				throw new Error("Invalid layout data: page and metadata are required");
			}
			const response = await axios.post(
				`${API_BASE_URL}/create_page_layout`,
				layout,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createPageLayout");
		}
	},
	updatePageLayout: async (layout_id: string, layout: AddPageLayoutData) => {
		try {
			// Validate inputs
			if (!layout_id) {
				throw new Error("Invalid layout ID");
			}
			if (!layout.page || !layout.metadata) {
				throw new Error("Invalid layout data: page and metadata are required");
			}
			const response = await axios.post(`${API_BASE_URL}/update_page_layout`, {
				id: layout_id,
				...layout,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updatePageLayout");
		}
	},
	deletePageLayout: async (layout_id: string) => {
		try {
			// Validate layout ID
			if (!layout_id) {
				throw new Error("Invalid layout ID");
			}
			const response = await axios.delete(
				`${API_BASE_URL}/delete_page_layout/${layout_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deletePageLayout");
		}
	},
	fetchCategories: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_categories`);
			return response.data.results;
		} catch (error: any) {
			handleApiError(error, "fetchCategories");
		}
	},
	fetchCategoryProducts: async (
		categoryId: string,
		params?: {
			page?: number;
			per_page?: number;
			sort?: string;
			order?: string;
		},
	) => {
		try {
			const queryParams = new URLSearchParams();
			if (params?.page) queryParams.append("page", params.page.toString());
			if (params?.per_page)
				queryParams.append("per_page", params.per_page.toString());
			if (params?.sort) queryParams.append("sort", params.sort);
			if (params?.order) queryParams.append("order", params.order);

			const url = `${API_BASE_URL}/category/${categoryId}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
			const response = await axios.get(url);
			return response.data.results;
		} catch (error: any) {
			handleApiError(error, "fetchCategoryProducts");
		}
	},
	getUser: async (email: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/find_user/${email}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUser");
		}
	},
	getChangeLogs: async (page: number, limit: number, is_active?: boolean) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_changelogs`, {
				params: is_active ? { page, limit, is_active } : { page, limit },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getChangeLogs");
		}
	},
	createChangeLog: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/create_changelog`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createChangeLog");
		}
	},
	updateChangeLog: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_changelog`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateChangeLog");
		}
	},
	getCoupons: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_coupons`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getCoupons");
		}
	},
	updateCouponStatus: async (
		id: number,
		isActive: boolean,
		user_id: string,
	) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/update_coupon`, {
				id,
				is_active: isActive,
				updated_by: user_id,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateCouponStatus");
		}
	},
	addCoupon: async (coupon: CouponType) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/create_coupon`,
				coupon,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "addCoupon");
		}
	},
	updateCoupon: async (coupon: CouponType) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_coupon`,
				coupon,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateCoupon");
		}
	},
	uploadPrescriptions: async (order_id: string) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/get_prescriptions/${order_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "Upload Prescriptions");
		}
	},
	getDelhiveryJson: async (awbNumber: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_delhivery_json/${awbNumber}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getDelhiveryJson");
		}
	},
	getQueueCounts: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/queues`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getQueueCounts");
		}
	},
	getQueueJobs: async (
		status: string,
		page: number,
		limit: number,
		search: string,
		queue?: string,
	) => {
		try {
			const params: any = { page, limit, search };
			if (queue) {
				params.queue = queue;
			}
			const response = await axios.get(`${API_BASE_URL}/queues/${status}`, {
				params,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getQueueJobs");
		}
	},
	retryQueueJob: async (jobId: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/queues/retry`, {
				jobId,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "retryQueueJob");
		}
	},
	getStateWiseStats: async (filters: any) => {
		try {
			const encryptedFilters = await encrypt(filters);
			const response = await axios.get(`${API_BASE_URL}/get_state_wise_stats`, {
				params: { filters: encryptedFilters },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getStateWiseStats");
		}
	},
	getFilters: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_filters`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getFilters");
		}
	},
	getMedsCategoryList: async (
		page: number,
		limit: number,
		search?: string,
		tab?: "unmapped" | "mapped",
	) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_all_meds_category`,
				{
					params: {
						page,
						limit,
						...(search && { search }),
						...(tab && { tab }),
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getMedsCategoryList");
		}
	},
	updateMedsCategory: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_meds_category`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateMedsCategory");
		}
	},
	cancelOrder: async (
		order_id: string,
		user_id: string,
		reject_reason = "",
	) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/cancel_order`, {
				order_id,
				user_id,
				reject_reason,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "cancelOrder");
		}
	},
	markAsCod: async (invoice_number: string, is_prepaid: boolean) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/mark_as_cod/${invoice_number}`,
				{
					is_prepaid,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "markAsCod");
		}
	},
	getMappedMeds: async (
		page: number,
		limit: number,
		category: string,
		entity_id: number,
		user_id: string,
	) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_mapped_meds`, {
				params: { page, limit, category, entity_id, user_id },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getMappedMeds");
		}
	},
	searchMasterMeds: async (
		searchTerm: string,
		med_id: number,
		entity_id: number,
		user_id: string,
	) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/search_master_meds`, {
				params: { searchTerm, med_id, entity_id, user_id },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchMasterMeds");
		}
	},
	mapMed: async (med_id: number, master_med_id: number, user_id: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/map_med`, {
				med_id,
				master_med_id,
				user_id,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "mapMed");
		}
	},
	uploadMedsExcel: async (formData: FormData) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/upload_meds_excel`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "uploadMedsExcel");
		}
	},
	uploadFile: async (formData: FormData) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/upload_file`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "uploadFile");
		}
	},
	downloadMappedMedsExcel: async (entity_id: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/download_mapped_meds_excel`,
				{
					params: { entity_id },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadMappedMedsExcel");
		}
	},
	getFastMovingMeds: async (page: number, limit: number, entity_id: number, user_id: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_fast_moving_meds`, {
				params: { page, limit, entity_id, user_id },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getFastMovingMeds");
		}
	},
	searchPharmacyProducts: async (searchTerm: string, entity_id: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/search_pharmacy_products`,
				{
					params: { searchTerm, entity_id },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchPharmacyProducts");
		}
	},
	getMasterMeds: async (searchTerm: string, page: number, limit: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_master_meds`, {
				params: { searchTerm, page, limit },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getMasterMeds");
		}
	},
	getProductStock: async (
		searchTerm: string,
		page: number,
		limit: number,
		entity_id: number,
		user_id: string,
	) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_product_stock`, {
				params: { searchTerm, page, limit, entity_id, user_id },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getProductStock");
		}
	},
	getProductStockEntities: async () => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_product_stock_entities`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getProductStockEntities");
		}
	},
	updateProductAutoStock: async (id: number, is_auto_stocked: boolean) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_product_auto_stock`,
				{
					id,
					is_auto_stocked,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateProductAutoStock");
		}
	},
	updateProductStock: async (data: {
		id: number;
		new_stock: number;
		reason: string;
		updated_by: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_product_stock`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateProductStock");
    }
  },
	searchProductsMasterForStock: async (search: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/search_products_master_for_stock`,
				{ params: { search } },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchProductsMasterForStock");
		}
	},
	createProductStockEntry: async (entity_id: number, product_master_id: number) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/create_product_stock_entry`,
				{ entity_id, product_master_id },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createProductStockEntry");
		}
	},
	pushToSmartPharma: async (trackingId: string) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_PILLPLUS_API_URL}/integration/pushToSmartPharma`,
				{
					trackingId,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "pushToSmartPharma");
		}
	},
	pushToMyrx: async (trackingId: string) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_PILLPLUS_API_URL}/integration/pushToMyrx`,
				{
					trackingId,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "pushToMyrx");
		}
	},

	updateMasterMed: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_master_med`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateMasterMed");
		}
	},
	checkBulkTransferEligibility: async (data: {
		invoice_numbers: string[];
		pharmacy_id: number;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/check_bulk_transfer_eligibility`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "checkBulkTransferEligibility");
		}
	},
	bulkTransferOrders: async (data: {
		invoice_numbers: string[];
		pharmacy_id: number;
		user_id: string;
		transfer_reason?: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/bulk_transfer_orders`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkTransferOrders");
		}
	},
	getOrdersExcelFile: async (data: {
		start_date: string;
		end_date: string;
		mode: string;
		status: string;
		entity?: number;
		entities?: string;
		providers?: string;
		user_id?: string;
	}) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_orders_excel`, {
				params: data,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getOrdersExcelFile");
		}
	},
	retriggerOrderStatus: async (order_id: number, status: string) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/retrigger_order_status`,
				{
					order_id,
					status,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "retriggerOrderStatus");
		}
	},
	downloadMedsCategoryExcel: async () => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/download_meds_category_excel`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadMedsCategoryExcel");
		}
	},
	downloadRejectedOrdersExcel: async (params?: {
		keyword?: string;
		start_date?: string;
		end_date?: string;
	}) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/download_rejected_orders_excel`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadRejectedOrdersExcel");
		}
	},
	downloadMissingProductStockExcel: async (params?: {
		keyword?: string;
		start_date?: string;
		end_date?: string;
	}) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/download_missing_product_stock_excel`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadMissingProductStockExcel");
		}
	},
	deleteMedsCategory: async (id: number) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/delete_meds_category/${id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteMedsCategory");
		}
	},
	getLatestRequestId: async (product_id: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_latest_request_id`,
				{
					params: { product_id },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getLatestRequestId");
		}
	},
	getOrdersWithMissingAttachments: async (data: {
		start_date: string;
		end_date: string;
	}) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_orders_with_missing_attachments`,
				{
					params: data,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getOrdersWithMissingAttachments");
		}
	},
	bulkCancelOrders: async (data: {
		invoice_numbers: string[];
		reject_reason: string;
		user_email: string;
		user_id: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/bulk_cancel_orders`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkCancelOrders");
		}
	},
	bulkUpdateOrderStatus: async (data: {
		invoice_numbers: string[];
		status: string;
		user_id: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/bulk_update_order_status`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkUpdateOrderStatus");
		}
	},
	getBulkOrderStatus: async (invoice_numbers: string[]) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/get_bulk_order_status`,
				{
					invoice_numbers,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getBulkOrderStatus");
		}
	},
	// Entity Margins API
	getEntityMargins: async (entityId: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/entity_margins/${entityId}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getEntityMargins");
		}
	},
	createEntityMargin: async (data: {
		entity_id: number;
		product_category_id: string;
		margin: number;
		updated_by: string;
	}) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/entity_margins`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createEntityMargin");
		}
	},
	updateEntityMargin: async (
		id: string,
		data: {
			margin: number;
			updated_by: string;
		},
	) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/entity_margins/${id}`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateEntityMargin");
		}
	},
	deleteEntityMargin: async (id: string) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/entity_margins/${id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteEntityMargin");
		}
	},
	getShipmentDetails: async (invoice_number: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_shipment_details/${invoice_number}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getShipmentDetails");
		}
	},
	getLogisticPartners: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_logistic_partners`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getLogisticPartners");
		}
	},
	updateAwbDetails: async (data: {
		invoice_number: string;
		awb_number: string;
		transporter: string;
		user_id: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_awb_details`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateAwbDetails");
		}
	},
	createMasterMed: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/create_master_med`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createMasterMed");
		}
	},
	uploadMasterMedsExcel: async (formData: FormData) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/upload_master_meds_excel`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "uploadMasterMedsExcel");
		}
	},
	// B2C Products Mapping APIs
	getB2CMappedProducts: async (
		page: number,
		limit: number,
		category: string,
		entity: any,
		showOnlyOtc: boolean = false,
		searchQuery: string = "",
		showMRPZero: boolean = false,
	) => {
		try {
			const entity_id = entity?.value || 0;
			const response = await axios.get(
				`${API_BASE_URL}/get_b2c_mapped_products`,
				{
					params: {
						page,
						limit,
						category,
						entity_id,
						show_only_otc: showOnlyOtc,
						search: searchQuery,
						show_mrp_zero: showMRPZero,
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getB2CMappedProducts");
		}
	},
	searchProductsMaster: async (search: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/search_products_master`,
				{
					params: { search },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchProductsMaster");
		}
	},
	searchB2CProducts: async (search: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/search_b2c_products`, {
				params: { search },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchB2CProducts");
		}
	},
	mapB2CProduct: async (data: {
		entity_product_id: string;
		tp_code?: string;
		med_products_id?: number;
	}) => {
		try {
			// Get user information from localStorage
			const userStr = localStorage.getItem(STORAGE_KEY);
			const user = userStr ? JSON.parse(userStr) : null;

			const response = await axios.post(
				`${API_BASE_URL}/map_b2c_product`,
				data,
				{
					headers: {
						"X-User-Id": user?.id || "",
						"X-User-Email": user?.email || "",
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "mapB2CProduct");
		}
	},

	// B2C Products Master
	getB2CMasterProducts: async (
		searchTerm: string,
		page: number,
		limit: number,
		options?: { signal?: AbortSignal; timeoutMs?: number },
	) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_b2c_master_products`,
				{
					params: { searchTerm, page, limit },
					// Support request cancellation and short client-side timeout
					signal: options?.signal,
					timeout: options?.timeoutMs ?? 15000,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getB2CMasterProducts");
		}
	},

	updateB2CMasterProduct: async (data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_b2c_master_product`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateB2CMasterProduct");
		}
	},

	updateB2CDescription: async (
		mb_code: number | string,
		description: string,
	) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_b2c_description`,
				{ mb_code, description },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateB2CDescription");
		}
	},

	updateB2CProductStatus: async (id: number, is_active: boolean) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_b2c_product_status`,
				{ id, is_active },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateB2CProductStatus");
		}
	},

	updateB2CDiscontinuedStatus: async (id: number, discontinued: boolean) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/update_b2c_discontinued_status`,
				{ id, discontinued },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateB2CDiscontinuedStatus");
		}
	},
	deletePharmacyProduct: async (id: number) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/delete_pharmacy_product/${id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deletePharmacyProduct");
		}
	},
	getCartCharges: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/cart_charges`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getCartCharges");
		}
	},
	createCartCharges: async (data: any) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/cart_charges`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createCartCharges");
		}
	},
	updateCartCharges: async (data: any) => {
		try {
			const { id, ...updateData } = data;
			const response = await axios.put(
				`${API_BASE_URL}/cart_charges/${id}`,
				updateData,
			);

			return response.data;
		} catch (error: any) {
			// Return the error response directly instead of throwing
			if (axios.isAxiosError(error) && error.response) {
				return error.response.data;
			}
			handleApiError(error, "updateCartCharges");
		}
	},
	deleteCartCharges: async (id: number) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/cart_charges/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteCartCharges");
		}
	},
	getNotificationRequests: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		isRead?: boolean;
		startDate?: string;
		endDate?: string;
	}) => {
		try {
			const queryParams = new URLSearchParams();
			if (params?.page) queryParams.append("page", params.page.toString());
			if (params?.limit) queryParams.append("limit", params.limit.toString());
			if (params?.search) queryParams.append("search", params.search);
			if (params?.isRead !== undefined)
				queryParams.append("is_read", params.isRead.toString());
			if (params?.startDate) queryParams.append("startDate", params.startDate);
			if (params?.endDate) queryParams.append("endDate", params.endDate);

			const url = `${API_BASE_URL}/notification_requests${
				queryParams.toString() ? `?${queryParams.toString()}` : ""
			}`;
			const response = await axios.get(url);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getNotificationRequests");
		}
	},
	markNotificationAsRead: async (id: string) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/notification_requests/${id}/mark-read`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "markNotificationAsRead");
		}
	},
	getGroupedNotificationsByType: async (params?: {
		isRead?: boolean;
		search?: string;
	}) => {
		try {
			const queryParams = new URLSearchParams();
			if (params?.isRead !== undefined)
				queryParams.append("is_read", params.isRead.toString());
			if (params?.search) queryParams.append("search", params.search);

			const url = `${API_BASE_URL}/notification_requests/grouped${
				queryParams.toString() ? `?${queryParams.toString()}` : ""
			}`;
			const response = await axios.get(url);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getGroupedNotificationsByType");
		}
	},
	downloadNotificationRequestsExcel: async (params?: {
		isRead?: boolean;
		search?: string;
		startDate?: string;
		endDate?: string;
	}) => {
		try {
			const queryParams = new URLSearchParams();
			if (params?.isRead !== undefined)
				queryParams.append("is_read", params.isRead.toString());
			if (params?.search) queryParams.append("search", params.search);
			if (params?.startDate) queryParams.append("startDate", params.startDate);
			if (params?.endDate) queryParams.append("endDate", params.endDate);

			const url = `${API_BASE_URL}/notification_requests/download${
				queryParams.toString() ? `?${queryParams.toString()}` : ""
			}`;
			const response = await axios.get(url);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadNotificationRequestsExcel");
		}
	},
	getSupportCases: async (
		page: number = 1,
		limit: number = 10,
		filters?: {
			search?: string;
			status?: string;
			priority?: string;
			topic?: string;
			createdDate?: string;
		},
	) => {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
			});

			if (filters?.search) params.append("search", filters.search);
			if (filters?.status) params.append("status", filters.status);
			if (filters?.priority) params.append("priority", filters.priority);
			if (filters?.topic) params.append("topic", filters.topic);
			if (filters?.createdDate)
				params.append("createdDate", filters.createdDate);

			const response = await axios.get(
				`${API_BASE_URL}/support_cases?${params.toString()}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getSupportCases");
		}
	},
	getSupportCase: async (id: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/support_cases/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getSupportCase");
		}
	},
	updateSupportCase: async (id: string, data: any) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/support_cases/${id}`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateSupportCase");
		}
	},

	// Non-Operational Days API
	getNonOperationalDays: async (params?: {
		start_date?: string;
		end_date?: string;
		page?: number;
		limit?: number;
	}) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/non_operational_days`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getNonOperationalDays");
		}
	},

	createNonOperationalDay: async (data: {
		non_operational_date: string;
		reason: string;
		user_id: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/non_operational_days`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createNonOperationalDay");
		}
	},

	updateNonOperationalDay: async (
		id: number,
		data: { non_operational_date?: string; reason?: string; user_id: string },
	) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/non_operational_days/${id}`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateNonOperationalDay");
		}
	},

	deleteNonOperationalDay: async (id: number, user_id: string) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/non_operational_days/${id}`,
				{
					data: { user_id },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteNonOperationalDay");
		}
	},

	bulkCreateNonOperationalDays: async (
		days: Array<{ non_operational_date: string; reason: string }>,
	) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/non_operational_days/bulk`,
				{ days },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "bulkCreateNonOperationalDays");
		}
	},
	// Non-Operational Pincodes API
	getNonOperationalPincodes: async (params?: {
		start_date?: string;
		end_date?: string;
		page?: number;
		limit?: number;
	}) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/non_operational_pincodes`,
				{ params },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getNonOperationalPincodes");
		}
	},

	createNonOperationalPincode: async (data: {
		pincodes: string[];
		start_datetime: string;
		end_datetime: string;
		buffer_hours: number;
		reason: string;
		user_id: string;
	}) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/non_operational_pincodes`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createNonOperationalPincode");
		}
	},

	updateNonOperationalPincode: async (
		id: number,
		data: {
			pincodes?: string[];
			start_datetime?: string;
			end_datetime?: string;
			buffer_hours?: number;
			reason?: string;
			user_id: string;
		},
	) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/non_operational_pincodes/${id}`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateNonOperationalPincode");
		}
	},

	deleteNonOperationalPincode: async (id: number, user_id: string) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/non_operational_pincodes/${id}`,
				{
					data: { user_id },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteNonOperationalPincode");
		}
	},
	getSupportMessages: async (caseId: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/support_cases/${caseId}/messages`,
			);
			return response.data.results || [];
		} catch (error: any) {
			handleApiError(error, "getSupportMessages");
		}
	},
	createSupportMessage: async (caseId: string, data: any) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/support_cases/${caseId}/messages`,
				data,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createSupportMessage");
		}
	},
	getSupportCaseStats: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/support_cases/stats`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getSupportCaseStats");
		}
	},
	downloadSupportCasesExcel: async (filters?: {
		search?: string;
		status?: string;
		priority?: string;
		topic?: string;
		createdDate?: string;
	}) => {
		try {
			const params = new URLSearchParams();
			if (filters?.search) params.append("search", filters.search);
			if (filters?.status) params.append("status", filters.status);
			if (filters?.priority) params.append("priority", filters.priority);
			if (filters?.topic) params.append("topic", filters.topic);
			if (filters?.createdDate)
				params.append("createdDate", filters.createdDate);

			const response = await axios.get(
				`${API_BASE_URL}/support_cases/download_excel${params.toString() ? `?${params.toString()}` : ""}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "downloadSupportCasesExcel");
		}
	},

	// B2C Product Mapping APIs
	getProductMappingStatistics: async (
		entityId: number | null,
		showOnlyOtc: boolean = false,
		showMrpZero: boolean = false,
	) => {
		try {
			const params = new URLSearchParams();
			if (entityId !== null) {
				params.append("entity_id", entityId.toString());
			}
			params.append("show_only_otc", showOnlyOtc.toString());
			params.append("show_mrp_zero", showMrpZero.toString());

			const response = await axios.get(
				`${API_BASE_URL}/b2c-mapping/entity-products/statistics?${params.toString()}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getProductMappingStatistics");
		}
	},

	getMappedEntityProducts: async (
		entityId: number,
		page: number = 1,
		limit: number = 20,
		showOnlyOtc: boolean = false,
		searchQuery: string = "",
	) => {
		try {
			const params = new URLSearchParams();
			params.append("entity_id", entityId.toString());
			params.append("page", page.toString());
			params.append("limit", limit.toString());
			params.append("show_only_otc", showOnlyOtc.toString());
			if (searchQuery) params.append("search", searchQuery);

			const response = await axios.get(
				`${API_BASE_URL}/b2c-mapping/entity-products/mapped?${params.toString()}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getMappedEntityProducts");
		}
	},

	getUnmappedEntityProducts: async (
		entityId: number,
		page: number = 1,
		limit: number = 20,
		showOnlyOtc: boolean = false,
		searchQuery: string = "",
	) => {
		try {
			const params = new URLSearchParams();
			params.append("entity_id", entityId.toString());
			params.append("page", page.toString());
			params.append("limit", limit.toString());
			params.append("show_only_otc", showOnlyOtc.toString());
			if (searchQuery) params.append("search", searchQuery);

			const response = await axios.get(
				`${API_BASE_URL}/b2c-mapping/entity-products/unmapped?${params.toString()}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUnmappedEntityProducts");
		}
	},

	getNoMatchFoundEntityProducts: async (
		entityId: number | null,
		page: number = 1,
		limit: number = 20,
		showOnlyOtc: boolean = false,
		searchQuery: string = "",
		showMrpZero: boolean = false,
	) => {
		try {
			const params = new URLSearchParams();
			params.append("entity_id", (entityId || 0).toString());
			params.append("page", page.toString());
			params.append("limit", limit.toString());
			params.append("show_only_otc", showOnlyOtc.toString());
			params.append("show_mrp_zero", showMrpZero.toString());
			if (searchQuery) params.append("search", searchQuery);

			const response = await axios.get(
				`${API_BASE_URL}/b2c-mapping/entity-products/no-match-found?${params.toString()}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getNoMatchFoundEntityProducts");
		}
	},

	moveProductsToNoMatchFound: async (productIds: string[]) => {
		try {
			console.log("[API] moveProductsToNoMatchFound called with:", productIds);
			// Get user from localStorage
			const storedUser = localStorage.getItem(STORAGE_KEY);
			const user = storedUser ? JSON.parse(storedUser) : null;
			console.log("[API] User from localStorage:", user);

			const payload = {
				product_ids: productIds,
			};
			const headers = {
				"X-User-Id": user?.id || "",
				"X-User-Email": user?.email || "",
			};
			console.log("[API] Request payload:", payload);
			console.log("[API] Request headers:", headers);

			const response = await axios.post(
				`${API_BASE_URL}/b2c-mapping/products/move-to-no-match-found`,
				payload,
				{ headers },
			);
			console.log("[API] Response:", response.data);
			return response.data;
		} catch (error: any) {
			console.error("[API] Error in moveProductsToNoMatchFound:", error);
			handleApiError(error, "moveProductsToNoMatchFound");
		}
	},

	moveProductsBackToUnmapped: async (productIds: string[]) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/b2c-mapping/products/move-back-to-unmapped`,
				{
					product_ids: productIds,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "moveProductsBackToUnmapped");
		}
	},

	searchUpmProducts: async (
		query: string,
		page: number = 0,
		limit: number = 10,
		showOnlyOtc: boolean = false,
	) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/b2c-mapping/upm-products/search`,
				{
					query,
					page,
					limit,
					show_only_otc: showOnlyOtc,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "searchUpmProducts");
		}
	},

	getUPMMasterProducts: async (
		searchTerm: string,
		page: number,
		limit: number,
		options?: { signal?: AbortSignal; timeoutMs?: number },
	) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/upm-masters/get_upm_master_products`,
				{
					params: { searchTerm, page, limit },
					signal: options?.signal,
					timeout: options?.timeoutMs ?? 15000,
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUPMMasterProducts");
		}
	},

	createProductLink: async (entityProductId: string, upmProductId: string) => {
		try {
			// Get user from localStorage
			const storedUser = localStorage.getItem(STORAGE_KEY);
			const user = storedUser ? JSON.parse(storedUser) : null;

			const response = await axios.post(
				`${API_BASE_URL}/b2c-mapping/products/create-link`,
				{
					entity_product_id: entityProductId,
					upm_product_id: upmProductId,
				},
				{
					headers: {
						"X-User-Id": user?.id || "",
						"X-User-Email": user?.email || "",
					},
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createProductLink");
		}
	},

	removeProductLink: async (productId: string) => {
		try {
			const response = await axios.delete(
				`${API_BASE_URL}/b2c-mapping/products/remove-link/${productId}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "removeProductLink");
		}
	},
	markSupportCaseViewed: async (id: string) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/support_cases/${id}/viewed`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "markSupportCaseViewed");
		}
	},
	getMissingProductStock: async (
		page: number,
		limit: number,
		keyword: string,
		start_date: string,
		end_date: string,
	) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_missing_product_stock`,
				{
					params: { page, limit, keyword, start_date, end_date },
				},
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getMissingProductStock");
		}
	},
	markAsAutoStock: async (productId: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/mark_as_auto_stock`, {
				product_id: productId,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "markAsAutoStock");
		}
	},
	getB2COrders: async (params: URLSearchParams) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/get_b2c_orders`, {
				params,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getB2COrders");
		}
	},
	getB2COrderDetails: async (orderId: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/get_b2c_order_details/${orderId}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getB2COrderDetails");
		}
	},

	initiateRefund: async (payload: {
		orderId: string;
		paymentId: string;
		amount: number;
		reason: string;
		notes?: Record<string, any>;
	}) => {
		const response = await axios.post(
			`${API_BASE_URL}/refunds/initiate`,
			payload,
		);
		return response.data;
	},

	getRefundsByOrder: async (orderId: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/refunds/order/${orderId}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getRefundsByOrder");
		}
	},

	getRazorpayPaymentSummary: async (paymentId: string) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/razorpay/payment/${paymentId}/summary`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getRazorpayPaymentSummary");
		}
	},

	getB2COrdersExcel: async (params: URLSearchParams) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/download_b2c_orders_excel`, {
            params,
        });
        return response.data;
    } catch (error: any) {
        handleApiError(error, "getB2COrdersExcel");
    }
},

getGroupedB2COrders: async (params: URLSearchParams) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/get_grouped_b2c_orders`,
            {
                params,
            }
        );
        return response.data;
    } catch (error: any) {
        handleApiError(error, "getGroupedB2COrders");
    }
},

	getUpmMasters: async (master: string, query: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/upm-masters/${master}?search=${query}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUpmMasters");
		}
	},

	createUpmMasters: async (master: string, name: string) => {
		try {
			const user = localStorage.getItem(STORAGE_KEY);
			const userData = user ? JSON.parse(user) : null;
			const headers = {
				'X-User-Email': userData?.email || ''
			};
			const response = await axios.post(`${API_BASE_URL}/upm-masters/${master}`, { name }, { headers });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createUpmMasters");
		}
	},
	getUpmMoleculeCompositionMasters: async (query: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/upm-molecule-composition-masters?search=${query}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUpmMoleculeCompositionMasters");
		}
	},
	createUpmProduct: async (upmProductData: any) => {
		try {
			const user = localStorage.getItem(STORAGE_KEY);
			const userData = user ? JSON.parse(user) : null;
			const headers = {
				'X-User-Email': userData?.email || ''
			};
			const response = await axios.post(`${API_BASE_URL}/upm-products/create`, upmProductData, { headers });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createUpmProduct");
		}
	},

	// UPM Tags API
	getUpmTags: async (query: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/upm-tags?search=${query}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUpmTags");
		}
	},

	// UPM Variant Categories API
	getUpmVariantCategories: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/upm-variant-categories`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUpmVariantCategories");
		}
	},

	// UPM Variant Values API
	getUpmVariantValues: async (categoryId: string, query?: string) => {
		try {
			const params = new URLSearchParams({ categoryId });
			if (query) {
				params.append('search', query);
			}
			const response = await axios.get(`${API_BASE_URL}/upm-variant-values?${params}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUpmVariantValues");
		}
	},

	// Create UPM Variant Value
	createUpmVariantValue: async (name: string, categoryId: number) => {
		try {
			const user = localStorage.getItem(STORAGE_KEY);
			const userData = user ? JSON.parse(user) : null;
			const headers = {
				'X-User-Email': userData?.email || ''
			};
			const response = await axios.post(`${API_BASE_URL}/upm-variant-values`, {
				name,
				category_id: categoryId
			}, { headers });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createUpmVariantValue");
		}
	},

	// Entity Rules APIs
	getEntityRules: async (params?: {
		entity_id?: number;
		provider_id?: number;
		status?: "active" | "inactive";
		pincode?: string;
		page?: number;
		limit?: number;
	}) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/entity_rules`, {
				params,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getEntityRules");
		}
	},

	getEntityRuleById: async (id: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/entity_rules/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getEntityRuleById");
		}
	},

	createEntityRule: async (data: {
		entity_id: number;
		provider_id?: number;
		procurement?: boolean;
		pre_inventory_pharmacy_id?: number;
		fallback_pharmacy_id?: number;
		start_time?: string;
		end_time?: string;
		status?: "active" | "inactive";
		pincodes: string[];
		for_all_pincodes?: boolean;
		order_cap?: number;
	}) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.post(`${API_BASE_URL}/entity_rules`, data, {
				headers: { 'x-user-email': userEmail },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createEntityRule");
		}
	},

	updateEntityRule: async (
		id: number,
		data: {
			entity_id?: number;
			provider_id?: number | null;
			procurement?: boolean;
			pre_inventory_pharmacy_id?: number | null;
			fallback_pharmacy_id?: number | null;
			start_time?: string | null;
			end_time?: string | null;
			status?: "active" | "inactive";
			pincodes?: string[];
			for_all_pincodes?: boolean;
			order_cap?: number | null;
		},
	) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.put(
				`${API_BASE_URL}/entity_rules/${id}`,
				data,
				{ headers: { 'x-user-email': userEmail } },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateEntityRule");
		}
	},

	deleteEntityRule: async (id: number) => {
		try {
			let userEmail = "unknown";
			const user = localStorage.getItem(STORAGE_KEY);
			if (user) {
				try {
					const parsedUser = JSON.parse(user);
					userEmail = parsedUser.email || parsedUser.id || "unknown";
				} catch (e) {
					console.error('[API] Error parsing user from localStorage:', e);
				}
			}
			const response = await axios.delete(`${API_BASE_URL}/entity_rules/${id}`, {
				headers: { 'x-user-email': userEmail },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteEntityRule");
		}
	},


	// Roles and Permissions API
	getRoles: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/roles`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getRoles");
		}
	},

	createRole: async (data: {
		name: string;
		description?: string;
	}) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/roles`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createRole");
		}
	},

	updateRole: async (id: number, data: {
		name?: string;
		description?: string;
	}) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/roles/${id}`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updateRole");
		}
	},

	deleteRole: async (id: number) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/roles/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deleteRole");
		}
	},

	getPermissionGroups: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/permission-groups`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getPermissionGroups");
		}
	},

	getRoutePermissions: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/route-permissions`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getRoutePermissions");
		}
	},

	getPermissionGroupsByRole: async (role_id: number) => {
		try {
			const response = await axios.get(
				`${API_BASE_URL}/roles/${role_id}/permission-groups`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getPermissionGroupsByRole");
		}
	},

	createPermissionGroup: async (data: {
		name: string;
		role_id?: number;
		route?: string;
	}) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/permission-groups`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createPermissionGroup");
		}
	},
 
	linkGroupToRole: async (role_id: number, group_id: number) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/roles/${role_id}/link-group/${group_id}`,
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "linkGroupToRole");
		}
	},

	updatePermissionGroup: async (id: number, data: {
		name?: string;
		route?: string;
	}) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/permission-groups/${id}`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updatePermissionGroup");
		}
	},

	deletePermissionGroup: async (id: number) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/permission-groups/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deletePermissionGroup");
		}
	},

	getPermissionsByGroup: async (group_id: number, role_id?: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/permissions/group/${group_id}`, {
				params: role_id ? { role_id } : undefined,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getPermissionsByGroup");
		}
	},

	createPermission: async (data: {
		permission: string;
		description?: string;
		permission_groups_ref: number;
		role_id?: number;
	}) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/permissions`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "createPermission");
		}
	},

	updatePermission: async (id: number, data: {
		permission?: string;
		description?: string;
	}) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/permissions/${id}`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "updatePermission");
		}
	},

	deletePermission: async (id: number) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/permissions/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "deletePermission");
		}
	},

	// Unlink permission from role without deleting permission
	unlinkPermissionFromRole: async (role_id: number, permission_id: number) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/role-permissions`, {
				params: { role_id, permission_id },
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "unlinkPermissionFromRole");
		}
	},

	// User Role Management
	getUserRole: async (user_id: string) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/users/${user_id}/role`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getUserRole");
		}
	},

	assignRoleToUser: async (
		user_id: string,
		role_id: number,
		can_download_excel: boolean = false,
		can_upload_excel: boolean = false
	) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/users/${user_id}/role`, {
				role_id,
				can_download_excel,
				can_upload_excel,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "assignRoleToUser");
		}
	},

	removeRoleFromUser: async (user_id: string) => {
		try {
			const response = await axios.delete(`${API_BASE_URL}/users/${user_id}/role`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "removeRoleFromUser");
		}
	},
	// ─── B2C Products Management ────────────────────────────────────────

	b2cMgmtGetDashboard: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/dashboard`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetDashboard");
		}
	},

	b2cMgmtGetMRPIssues: async (page = 1, limit = 20) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/dashboard/mrp_issues`, { params: { page, limit } });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetMRPIssues");
		}
	},

	b2cMgmtGetUncategorized: async (page = 1, limit = 20) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/dashboard/uncategorized`, { params: { page, limit } });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetUncategorized");
		}
	},

	b2cMgmtGetDuplicateSummary: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/dashboard/duplicates`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetDuplicateSummary");
		}
	},

	b2cMgmtGetLinkStats: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/dashboard/link_stats`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetLinkStats");
		}
	},

	b2cMgmtGetProducts: async (params: Record<string, any>) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/products`, { params });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetProducts");
		}
	},

	b2cMgmtGetProductDetail: async (id: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetProductDetail");
		}
	},

	b2cMgmtUpdateFlags: async (id: number, flags: Record<string, boolean>, user: { email: string; name?: string }, reason?: string) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}/flags`, { id, flags, user, reason });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtUpdateFlags");
		}
	},

	b2cMgmtUpdatePrice: async (id: number, data: { mrp?: number; current_price?: number; user: { email: string; name?: string }; reason?: string }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}/price`, { id, ...data });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtUpdatePrice");
		}
	},

	b2cMgmtUpdateInfo: async (id: number, data: Record<string, any>) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}/info`, { id, ...data });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtUpdateInfo");
		}
	},

	b2cMgmtGetMRPHistory: async (id: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}/mrp_history`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetMRPHistory");
		}
	},

	b2cMgmtGetEntityProducts: async (id: number) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/products/${id}/entity_products`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetEntityProducts");
		}
	},

	b2cMgmtBulkFlags: async (data: { product_ids: number[]; flags: Record<string, boolean>; user: { email: string; name?: string }; reason?: string }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/products/bulk_flags`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtBulkFlags");
		}
	},

	b2cMgmtRecalculateDiscounts: async (data: { mdm_category?: string; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/products/recalculate_discounts`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtRecalculateDiscounts");
		}
	},

	// Data Review Queue
	b2cMgmtPopulateReviewQueue: async (type?: string) => {
		try {
			const url = type ? `${API_BASE_URL}/admin/b2c_mgmt/review/populate?type=${type}` : `${API_BASE_URL}/admin/b2c_mgmt/review/populate?type=all`;
			const response = await axios.post(url);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtPopulateReviewQueue");
		}
	},

	b2cMgmtGetReviewQueue: async (params: Record<string, any>) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/review/queue`, { params });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetReviewQueue");
		}
	},

	b2cMgmtGetReviewStats: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/review/stats`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetReviewStats");
		}
	},

	b2cMgmtAcceptReview: async (data: { ids: string[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/review/accept`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtAcceptReview");
		}
	},

	b2cMgmtRejectReview: async (data: { ids: string[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/review/reject`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtRejectReview");
		}
	},

	b2cMgmtApplyCustomValue: async (data: { id: string; custom_value: string; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/review/custom`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtApplyCustomValue");
		}
	},

	b2cMgmtConfirmMapping: async (data: { ids: string[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/review/confirm-mapping`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtConfirmMapping");
		}
	},

	b2cMgmtSkipReview: async (data: { ids: string[] }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/review/skip`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtSkipReview");
		}
	},

	// ─── UPM Sync ─────────────────────────────────────────────────
	upmSyncGetStats: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/upm_sync/stats`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncGetStats");
		}
	},
	upmSyncMolecules: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/molecules`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncMolecules");
		}
	},
	upmSyncBrands: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/brands`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncBrands");
		}
	},
	upmSyncManufacturers: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/manufacturers`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncManufacturers");
		}
	},
	upmSyncMedProducts: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/med_products`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncMedProducts");
		}
	},
	upmSyncCorrectFlags: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/correct_flags`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncCorrectFlags");
		}
	},
	upmSyncCreateProducts: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/create_products`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncCreateProducts");
		}
	},
	upmSyncRunAll: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/upm_sync/run_all`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "upmSyncRunAll");
		}
	},

	b2cMgmtGetEntityMRPDiscrepancies: async (params: Record<string, any>) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/entity_mrp_discrepancies`, { params });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetEntityMRPDiscrepancies");
		}
	},

	b2cMgmtGetDuplicates: async (params: Record<string, any>) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/duplicates`, { params });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetDuplicates");
		}
	},

	b2cMgmtReviewDuplicate: async (data: { med_product_id: number; med_entity_product_id: string; status: string; user: { email: string; name?: string }; notes?: string }) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/duplicates/review`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtReviewDuplicate");
		}
	},

	b2cMgmtGetCategories: async (includeInactive = false) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/categories`, { params: { include_inactive: includeInactive } });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetCategories");
		}
	},

	b2cMgmtCreateCategory: async (data: { name: string; slug?: string; parent_id?: string; is_otc_category?: boolean; is_health_condition?: boolean; show_in_home?: boolean; show_in_menu?: boolean; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/categories`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtCreateCategory");
		}
	},

	b2cMgmtUpdateCategory: async (id: string, data: Record<string, any>) => {
		try {
			const response = await axios.put(`${API_BASE_URL}/admin/b2c_mgmt/categories/${id}`, { id, ...data });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtUpdateCategory");
		}
	},

	b2cMgmtAssignCategories: async (data: { product_id: number; category_ids: string[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/product_categories`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtAssignCategories");
		}
	},

	b2cMgmtGetTags: async (search = "", includeInactive = false) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/tags`, { params: { search, include_inactive: includeInactive } });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetTags");
		}
	},

	b2cMgmtCreateTag: async (data: { tag_name: string; tag_type?: string; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/tags`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtCreateTag");
		}
	},

	b2cMgmtAssignTags: async (data: { product_id: number; tag_ids: string[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/product_tags`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtAssignTags");
		}
	},

	b2cMgmtGetChangeLog: async (params: Record<string, any>) => {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/change_log`, { params });
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtGetChangeLog");
		}
	},

	b2cMgmtExportProducts: async (branches?: string) => {
		try {
			const params: Record<string, string> = {};
			if (branches) params.branches = branches;
			const response = await axios.get(`${API_BASE_URL}/admin/b2c_mgmt/export`, {
				params,
				responseType: "blob",
			});
			return response;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtExportProducts");
		}
	},

	b2cMgmtImportPreview: async (fileBase64: string) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/import/preview`, {
				file_base64: fileBase64,
			});
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtImportPreview");
		}
	},

	b2cMgmtImportApply: async (data: { changes: any[]; user: { email: string; name?: string } }) => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/import/apply`, data);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtImportApply");
		}
	},

	b2cMgmtRetryTypesenseSync: async () => {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/b2c_mgmt/import/retry_sync`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "b2cMgmtRetryTypesenseSync");
		}
	},

	// ── Logistics Partners ──────────────────────────────────────────────────
	getLogisticsPartners: async () => {
		try {
			const response = await axios.get(`${API_BASE_URL}/logistics_partners`);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "getLogisticsPartners");
		}
	},

	toggleLogisticsPartner: async (id: number, active: boolean) => {
		try {
			const response = await axios.put(
				`${API_BASE_URL}/logistics_partners/${id}/toggle`,
				{ active },
			);
			return response.data;
		} catch (error: any) {
			handleApiError(error, "toggleLogisticsPartner");
		}
	},

};

export default api;
